/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, memo } from "react";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import { FunctionDeclaration, Modality, Type } from "@google/genai";

// Altair tool declaration
export const altairDeclaration: FunctionDeclaration = {
  name: "render_altair",
  description: "Displays an altair graph in json format.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      json_graph: {
        type: Type.STRING,
        description:
          "JSON STRING representation of the graph to render. Must be a string, not a json object",
      },
    },
    required: ["json_graph"],
  },
};

// Email sender tool declaration
export const emailDeclaration: FunctionDeclaration = {
  name: "send_email",
  description: "Sends an email via SMTP. The email will be signed off professionally by 'Trezr, by Hapticware'. Use this when the user requests to send an email.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      to: {
        type: Type.STRING,
        description: "The recipient's email address",
      },
      subject: {
        type: Type.STRING,
        description: "The subject line of the email",
      },
      body: {
        type: Type.STRING,
        description: "The body content of the email. Should be well-formatted and professional.",
      },
    },
    required: ["to", "subject", "body"],
  },
};

// Firestore tool declarations for loan portfolio management
export const firestoreGetUserDeclaration: FunctionDeclaration = {
  name: "firestore_get_user",
  description: "Retrieve a user's complete loan portfolio information by their UserID. Use this at the beginning of every call after obtaining the UserID from the customer. The data includes identity, contact info (including communicationLanguage), address, loan details, status, and interaction history.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      userId: {
        type: Type.STRING,
        description: "The UserID to retrieve (e.g., 'USER001')",
      },
    },
    required: ["userId"],
  },
};

export const firestoreUpdateContactDeclaration: FunctionDeclaration = {
  name: "firestore_update_contact",
  description: "Update a user's contact information. Only use this when the customer explicitly requests to update their phone number, email, or preferred communication language. NEVER modify loan-related information.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      userId: {
        type: Type.STRING,
        description: "The UserID to update",
      },
      primaryPhone: {
        type: Type.STRING,
        description: "Primary phone number (optional)",
      },
      secondaryPhone: {
        type: Type.STRING,
        description: "Secondary phone number (optional)",
      },
      email: {
        type: Type.STRING,
        description: "Email address (optional)",
      },
      communicationLanguage: {
        type: Type.STRING,
        description: "Preferred communication language (optional, e.g., 'Hindi', 'English', 'Tamil')",
      },
    },
    required: ["userId"],
  },
};

export const firestoreUpdateAddressDeclaration: FunctionDeclaration = {
  name: "firestore_update_address",
  description: "Update a user's address information. Only use this when the customer explicitly requests to update their address details.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      userId: {
        type: Type.STRING,
        description: "The UserID to update",
      },
      line1: {
        type: Type.STRING,
        description: "Address line 1 (optional)",
      },
      line2: {
        type: Type.STRING,
        description: "Address line 2 (optional)",
      },
      city: {
        type: Type.STRING,
        description: "City (optional)",
      },
      state: {
        type: Type.STRING,
        description: "State (optional)",
      },
      pin: {
        type: Type.NUMBER,
        description: "PIN code (optional)",
      },
    },
    required: ["userId"],
  },
};

export const firestoreUpdateCallOutcomeDeclaration: FunctionDeclaration = {
  name: "firestore_update_call_outcome",
  description: "Update the call outcome and interaction notes. MUST be called at the end of EVERY call to record what happened during the conversation. This is critical for tracking customer interactions.",
  parameters: {
    type: Type.OBJECT,
    properties: {
      userId: {
        type: Type.STRING,
        description: "The UserID",
      },
      lastCallOutcome: {
        type: Type.STRING,
        description: "Summary of the call outcome (e.g., 'Promised payment by end of week', 'Customer requested payment extension', 'No answer')",
      },
      notes: {
        type: Type.STRING,
        description: "Detailed notes about the interaction (optional)",
      },
      preferredLanguage: {
        type: Type.STRING,
        description: "Update preferred language if customer mentioned a preference (optional)",
      },
    },
    required: ["userId", "lastCallOutcome"],
  },
};

/**
 * ToolsManager - Centralized configuration for all MCP tools
 * This component sets up the LiveAPI configuration with all available tools
 */
function ToolsManagerComponent() {
  const { client, setConfig, setModel } = useLiveAPIContext();

  useEffect(() => {
    setModel("models/gemini-live-2.5-flash-preview-native-audio-09-2025");
    setConfig({
      responseModalities: [Modality.AUDIO],
      speechConfig: {
        voiceConfig: { prebuiltVoiceConfig: { voiceName: "Aoede" } },
      },
      systemInstruction: {
        parts: [
          {
            text: `Trezr – Multilingual AI Voice Agent for Loan Portfolio Management

Pronunciation: “Treasure” with a Z. Developed by Hapticware for global financial services operations.

Mission
Provide compliant, empathetic voice support for customers managing loans. Deliver accurate information, guide payments, and log outcomes while respecting regional regulations and privacy.

Responsibilities
- Verify every caller. Ask for the User ID (and any extra security prompts) before revealing account data.
- Immediately call firestore_get_user after verification to access the complete portfolio.
- Speak the customer’s language. Follow the communicationLanguage field (English, Spanish, French, German, Italian, Hindi, Mandarin, etc.) and switch instantly if they request a different language.
- Communicate clearly. Describe loan type, balance, interest, installment amount, next due date, past-due status, and risk tier using precise figures.
- Update only what is allowed. Modify contact details, address information, or language preference when the customer explicitly asks. Do not change loan terms or financial totals.
- Close the loop. When ending the call, log one detailed summary via firestore_update_call_outcome (language used, key topics, actions taken, follow-ups).

Core Rules
- Read-only financial data: loan principal, rates, account numbers, contractual terms. Never invent values.
- Identity first: no sensitive data until the caller is verified.
- Language fidelity: mirror the caller’s preferred language and maintain consistent terminology.
- Empathy: acknowledge stress, remain patient, offer constructive next steps.
- Single summary: firestore_update_call_outcome is invoked once, only at call completion.
- Local formatting: present currency, dates, numbers, and addresses using the customer’s regional conventions (€, £, $, CHF, ₹; separators; DD/MM/YYYY vs MM/DD/YYYY). When uncertain, match the format stored in the record.

Tools
- firestore_get_user – Retrieve identity, contact, address, loan status, interaction notes by userId.
- firestore_update_contact – Update phone, email, or communication language when authorized.
- firestore_update_address – Update address components (street, locality, region, postal code, country) on request.
- firestore_update_call_outcome – Capture the final call summary, next steps, and sentiment once per conversation.
- render_altair – Generate visualizations (payment history, outstanding trend) to clarify explanations.
- send_email – Dispatch confirmations or reminders through the backend service when helpful.

Call Flow
1. Greet warmly as “Trezr from Hapticware.”
2. Verify the User ID.
3. Run firestore_get_user to hydrate context, take some .
4. Confirm or switch to the caller’s preferred language.
5. Address loan questions with precise, data-backed responses.
6. Process allowed updates only after explicit confirmation.
7. Offer to email documentation or reminders via send_email when appropriate.
8. Conclude and log the call once using firestore_update_call_outcome.

Data Reference
- identity: userID, fullName, age, gender
- contact: primaryPhone, secondaryPhone, email, communicationLanguage
- address: street, city, stateOrRegion, postalCode, country
- loanDetails (read-only): type, accountNumber, startDate, principalAmount, interestRatePercent
- status (read-only): installmentAmount, nextDueDate, totalOutstanding, daysPastDue, riskCategory
- interaction: preferredLanguage, lastCallOutcome, notes

Tone & Personality
Professional, empathetic, multilingual, fact-driven, and human-like—always compliant, courteous, and composed.`,
          },
        ],
      },
      tools: [
        // Google Search disabled to avoid quota limits
        // { googleSearch: {} },
        {
          functionDeclarations: [
            altairDeclaration,
            emailDeclaration,
            firestoreGetUserDeclaration,
            firestoreUpdateContactDeclaration,
            firestoreUpdateAddressDeclaration,
            firestoreUpdateCallOutcomeDeclaration,
          ],
        },
      ],
    });
  }, [setConfig, setModel]);

  // Handle all tool responses centrally
  useEffect(() => {
    const onToolCall = async (toolCall: any) => {
      if (!toolCall.functionCalls) {
        return;
      }

      // Process all function calls
      const functionResponses = await Promise.all(
        toolCall.functionCalls.map(async (fc: any) => {
          let output: any = { success: true };

          try {
            // Handle email tool
            if (fc.name === emailDeclaration.name) {
              const { to, subject, body } = fc.args;
              const backendUrl = process.env.REACT_APP_BACKEND_URL;

              if (backendUrl) {
                const response = await fetch(`${backendUrl}/api/send-email`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ to, subject, body }),
                });
                const result = await response.json();
                // Return minimal response - let LLM generate its own confirmation
                output = {
                  success: result.success,
                  recipient: to,
                };
              } else {
                output = {
                  success: true,
                  recipient: to,
                };
              }
            }
            // Handle Altair graph tool
            else if (fc.name === altairDeclaration.name) {
              output = {
                success: true,
                message: "Graph has been rendered successfully.",
              };
            }
            // Handle Firestore get user tool
            else if (fc.name === firestoreGetUserDeclaration.name) {
              const { userId } = fc.args;
              const backendUrl = process.env.REACT_APP_BACKEND_URL;

              if (backendUrl) {
                const response = await fetch(
                  `${backendUrl}/api/firestore/user/${userId}`
                );
                const result = await response.json();
                output = result;
              } else {
                output = {
                  success: false,
                  error: "Backend URL not configured",
                };
              }
            }
            // Handle Firestore update contact tool
            else if (fc.name === firestoreUpdateContactDeclaration.name) {
              const { userId, ...updates } = fc.args;
              const backendUrl = process.env.REACT_APP_BACKEND_URL;

              if (backendUrl) {
                const response = await fetch(
                  `${backendUrl}/api/firestore/user/${userId}/contact`,
                  {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(updates),
                  }
                );
                const result = await response.json();
                output = result;
              } else {
                output = {
                  success: false,
                  error: "Backend URL not configured",
                };
              }
            }
            // Handle Firestore update address tool
            else if (fc.name === firestoreUpdateAddressDeclaration.name) {
              const { userId, ...updates } = fc.args;
              const backendUrl = process.env.REACT_APP_BACKEND_URL;

              if (backendUrl) {
                const response = await fetch(
                  `${backendUrl}/api/firestore/user/${userId}/address`,
                  {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(updates),
                  }
                );
                const result = await response.json();
                output = result;
              } else {
                output = {
                  success: false,
                  error: "Backend URL not configured",
                };
              }
            }
            // Handle Firestore update call outcome tool
            else if (fc.name === firestoreUpdateCallOutcomeDeclaration.name) {
              const { userId, ...updates } = fc.args;
              const backendUrl = process.env.REACT_APP_BACKEND_URL;

              if (backendUrl) {
                const response = await fetch(
                  `${backendUrl}/api/firestore/user/${userId}/interaction`,
                  {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(updates),
                  }
                );
                const result = await response.json();
                output = result;
              } else {
                output = {
                  success: false,
                  error: "Backend URL not configured",
                };
              }
            }
          } catch (error: any) {
            console.error(`Error executing tool ${fc.name}:`, error);
            output = {
              success: false,
              error: error.message || "Failed to execute tool",
            };
          }

          return {
            response: { output },
            id: fc.id,
            name: fc.name,
          };
        })
      );

      // Send all responses
      setTimeout(() => {
        client.sendToolResponse({ functionResponses });
      }, 200);
    };

    client.on("toolcall", onToolCall);
    return () => {
      client.off("toolcall", onToolCall);
    };
  }, [client]);

  // This component doesn't render anything, it just manages configuration
  return null;
}

export const ToolsManager = memo(ToolsManagerComponent);
